import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { KlarityMark } from "../components/ui/KlarityMark";
import { Menu } from "../components/ui/Menu";
import { api } from "../lib/api/endpoints";
import { can, useMe } from "../lib/auth";
import { useSection } from "./useSection";

/**
 * The rail's areas, in order (redesign spec §4.2). `bottom` items sit above the account button.
 * Phase 2 adds Quotes and Contracts after Clients: two entries here, nothing else in the shell.
 * Icons are the paths of the approved mockup, drawn on a 24 by 24 grid.
 */
export const RAIL_ITEMS = [
  {
    id: "clients",
    label: "Clients",
    to: "/clients",
    adminOnly: false,
    bottom: false,
    icon: <path d="M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M14 9h5a1 1 0 0 1 1 1v11M3 21h18M8 8h2M8 12h2M8 16h2" />,
  },
  {
    id: "internal",
    label: "Handbook",
    to: "/handbook",
    adminOnly: false,
    bottom: false,
    icon: <path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3zM5 17a3 3 0 0 1 3-3h11" />,
  },
  {
    id: "admin",
    label: "Admin",
    to: "/admin/topics",
    adminOnly: true,
    bottom: true,
    icon: (
      <>
        <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
        <circle cx="16" cy="7" r="2" />
        <circle cx="8" cy="17" r="2" />
      </>
    ),
  },
] as const;

type RailItem = (typeof RAIL_ITEMS)[number];

/** Up to two initials for the account button; "?" when the account has no name. */
export function initials(name: string | undefined): string {
  const letters = (name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return letters || "?";
}

/**
 * The dark rail at the left: the main areas, Admin for admins, and the account menu (redesign spec §4.2).
 * With `onToggleFolded` it also holds the button that folds the column beside it. The button is in the rail
 * because the rail is on every page, the document page included; a phone has the drawer and no such button.
 */
export function Rail({
  onNavigate,
  folded = false,
  onToggleFolded,
}: {
  onNavigate?: () => void;
  folded?: boolean;
  onToggleFolded?: () => void;
}) {
  const me = useMe();
  const { section, admin } = useSection();
  const signOut = async () => {
    await api.logout().catch(() => {});
    window.location.assign("/signin");
  };
  const shown = RAIL_ITEMS.filter((item) => !item.adminOnly || can(me, "admin"));
  const link = (item: RailItem) => {
    const current = item.id === "admin" ? admin : !admin && section === item.id;
    return (
      <Link
        key={item.id}
        to={item.to}
        onClick={onNavigate}
        aria-current={current ? "page" : undefined}
        className={clsx(
          "flex flex-col items-center gap-1 rounded-lg px-0.5 py-2.5 text-2xs focus-visible:outline-green-wash",
          current
            ? "bg-rail-active font-semibold text-white"
            : "text-rail-ink hover:bg-rail-active/60 hover:text-white",
        )}
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {item.icon}
        </svg>
        {item.label}
      </Link>
    );
  };
  return (
    <nav aria-label="Main" className="flex h-full w-21 shrink-0 flex-col gap-1 overflow-y-auto bg-rail px-2 py-4">
      <Link
        to="/clients"
        onClick={onNavigate}
        aria-label="Klarity Docs"
        className="flex justify-center rounded-lg pt-1 pb-4 focus-visible:outline-green-wash"
      >
        <KlarityMark />
      </Link>
      {shown.filter((item) => !item.bottom).map(link)}
      <div className="mt-auto flex flex-col gap-1">
        {onToggleFolded ? (
          <button
            type="button"
            aria-label={folded ? "Show sidebar" : "Hide sidebar"}
            title={folded ? "Show sidebar" : "Hide sidebar"}
            aria-expanded={!folded}
            aria-controls="kd-column"
            onClick={onToggleFolded}
            className="hidden h-9 w-9 place-items-center self-center rounded-lg text-rail-ink hover:bg-rail-active/60 hover:text-white focus-visible:outline-green-wash md:grid"
          >
            {folded ? <PanelLeftOpen size={18} aria-hidden="true" /> : <PanelLeftClose size={18} aria-hidden="true" />}
          </button>
        ) : null}
        {shown.filter((item) => item.bottom).map(link)}
        <Menu
          label="Account"
          align="end"
          trigger={
            <button
              type="button"
              aria-label="Account"
              className="mt-2 grid h-9 w-9 place-items-center self-center rounded-full bg-green-wash text-xs font-semibold text-ink focus-visible:outline-green-wash"
            >
              {initials(me?.name)}
            </button>
          }
          items={[
            { id: "who", label: me ? `${me.name} (${me.role})` : "Signed in", disabled: true, onSelect: () => {} },
            { id: "out", label: "Sign out", onSelect: () => void signOut() },
          ]}
        />
      </div>
    </nav>
  );
}
